import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useEnterpriseAuth as useAuth } from './EnterpriseAuthContext';
import { apiUtils } from '../utils/apiUtils';
import { useRealtimeConnection } from '../hooks/useRealtimeConnection';

const NotificationContext = createContext();

export function useNotification() {
  return useContext(NotificationContext);
}

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children, disabled = false }) {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info', notificationId: null });

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    // Skip system messages that were already handled by useWebSocket
    if (data.type === 'authenticated' || data.type === 'error') {
      return;
    }

    // Skip system messages
    if (data.type === 'connected' || data.type === 'pong') {
      return;
    }
    
    // Process actual notifications
    if (data.type === 'notification' && data.data) {
      const notification = data.data;
      
      // Add to notifications list
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);

      // Show snackbar notification
      setSnackbar({
        open: true,
        message: notification.message || notification.title || 'New notification',
        severity: notification.priority === 'critical' ? 'error' : 
                 notification.priority === 'high' ? 'warning' : 'info',
        notificationId: notification.id,
      });
    }
  }, []);

  // Initialize real-time connection (WebSocket only; no polling fallback)
  const {
    connected: wsConnected,
    authenticated: wsAuthenticated,
    error: wsError,
    connectionType,
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
    send: sendWebSocketMessage
  } = useRealtimeConnection({
    endpoint: '/api/notifications/ws',
    onMessage: handleWebSocketMessage,
    onAuthenticated: (data) => {
      // Connection authenticated successfully
      console.log('[NotificationContext] Connected via', data.type || 'websocket');
    },
    maxReconnectAttempts: 3,
    enableWebSocket: !disabled
  });

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;

    try {
      setLoading(true);
      const response = await apiUtils.get('/api/notifications?limit=50');
      setNotifications(response.notifications || []);
      updateUnreadCount(response.notifications || []);
    } catch (error) {
      console.error('[NotificationContext] Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Update unread count
  const updateUnreadCount = useCallback((notificationList) => {
    const count = notificationList.filter(n => !n.read && !n.is_read).length;
    setUnreadCount(count);
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await apiUtils.post('/api/notifications/mark-read', { notificationIds: [notificationId] });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('[NotificationContext] Error marking notification as read:', error);
    }
  }, []);

  // Acknowledge critical notification
  const acknowledgeNotification = useCallback(async (notificationId) => {
    try {
      // For now, we'll just mark as read since acknowledge endpoint doesn't exist
      await apiUtils.post('/api/notifications/mark-read', { notificationIds: [notificationId] });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, acknowledged: true, is_read: true, read: true } : n)
      );
      // Update unread count
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      // Close snackbar if it's showing this notification
      if (snackbar.notificationId === notificationId) {
        setSnackbar(s => ({ ...s, open: false }));
      }
    } catch (error) {
      console.error('[NotificationContext] Error acknowledging notification:', error);
    }
  }, [notifications, snackbar.notificationId]);

  // Legacy notify function for backward compatibility
  const notify = useCallback((message, severity = 'info') => {
    const notification = {
      id: `local-${Date.now()}`,
      message,
      title: severity.charAt(0).toUpperCase() + severity.slice(1),
      severity,
      priority: severity === 'error' ? 'high' : 'medium',
      read: false,
      created_at: new Date().toISOString(),
    };
    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);
    setSnackbar({ 
      open: true, 
      message, 
      severity,
      notificationId: notification.id 
    });
  }, []);


  // Connect WebSocket when authenticated
  useEffect(() => {
    if (!disabled && isAuthenticated && user?.id) {
      fetchNotifications();
      connectWebSocket();
    } else {
      disconnectWebSocket();
      setNotifications([]);
      setUnreadCount(0);
    }

    return () => {
      disconnectWebSocket();
    };
  }, [isAuthenticated, user?.id, disabled]);

  // Mark all as read
  const markAllRead = useCallback(async () => {
    try {
      await apiUtils.post('/api/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('[NotificationContext] Error marking all as read:', error);
    }
  }, []);

  const markAllAsRead = markAllRead; // Alias for compatibility

  // Close snackbar
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(s => ({ ...s, open: false }));
  };

  // Handle acknowledge button in snackbar
  const handleAcknowledge = () => {
    if (snackbar.notificationId) {
      acknowledgeNotification(snackbar.notificationId);
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    wsConnected,
    wsAuthenticated,
    wsError,
    connectionType,
    notify,
    fetchNotifications,
    markAsRead,
    acknowledgeNotification,
    markAllRead,
    markAllAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={handleClose}
          sx={{ fontSize: 16 }}
          action={
            snackbar.severity === 'error' && snackbar.notificationId ? (
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleAcknowledge}
                sx={{ fontWeight: 'bold' }}
              >
                ACKNOWLEDGE
              </Button>
            ) : null
          }
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}
